import { Box, Divider, List, ListItem, Tab, Tabs, Typography } from '@mui/material';
import dayjs from 'dayjs';
import numeral from 'numeral';
import { SyntheticEvent, useState } from 'react';
import { type ForecastDay } from '../../../shared/types';
import { useForecastDays } from '../../hooks/useForecasts';
import { Hours } from './Hours';
import { SplitButton } from '../SplitButton';
import { WeatherIcon } from '../WeatherIcon';

type ForecastDayProps = {
    day: ForecastDay;
};

const ForecastDayTab = ({ day }: ForecastDayProps) => {
    const { utcDate, maxTempCal, minTempCal, description, icon } = day;

    return (
        <ListItem dense={true}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle1" sx={(theme) => ({ textTransform: 'none', textAlign: 'center', color: theme.palette.text.primary })}>
                        {dayjs(utcDate).format('ddd MM/DD')}
                    </Typography>
                    {icon && <WeatherIcon icon={icon} description={description} />}
                </Box>
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="subtitle2" color="error">
                        {numeral(maxTempCal).format('0.0')}&#8457;
                    </Typography>
                    <Divider orientation="vertical" flexItem={true} sx={{ ml: 1, mr: 1 }} />
                    <Typography variant="subtitle2" color="info">
                        {numeral(minTempCal).format('0.0')}&#8457;
                    </Typography>
                </Box>
            </Box>
        </ListItem>
    );
};

type ForecastsProps = {
    id: string;
};

export const Forecasts = ({ id }: ForecastsProps) => {
    const { data: forecastDays = [] } = useForecastDays(id);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedFilterValue, setSelectedFilterValue] = useState(1);
    const filterOptions = [
        {
            label: 'Display Every Hour',
            value: 1,
        },
        {
            label: 'Display Every 3rd Hour',
            value: 3,
        },
        {
            label: 'Display Every 6th Hour',
            value: 6,
        },
    ];

    const handleTabSelect = (_: SyntheticEvent, tab: number) => {
        setSelectedTab(tab);
    };

    const handleFilterValue = (selectedValue: number) => {
        setSelectedFilterValue(selectedValue);
    };

    const selectedDate = forecastDays[selectedTab]?.utcDate.toString();
    const selectedDay = forecastDays[selectedTab];

    return (
        <>
            <Tabs value={selectedTab} onChange={handleTabSelect} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 74 }}>
                {forecastDays.map((values, index) => {
                    return <Tab key={values.utcDate.toString()} label={<ForecastDayTab day={values} />} id={`tab-${index}`} aria-controls={`tabpannel-${index}`} value={index} sx={{ p: 1 }} />;
                })}
            </Tabs>
            <Divider />
            {selectedDay && (
                <List>
                    {selectedDay.description && (
                        <ListItem dense={true} sx={{ pt: 1 }}>
                            <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Conditions:`}</Typography>
                            {selectedDay.icon && (
                                <Box sx={{ display: 'flex', mr: 0.5 }}>
                                    <WeatherIcon icon={selectedDay.icon} description={selectedDay.description} />
                                </Box>
                            )}
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {selectedDay.description}
                            </Typography>
                        </ListItem>
                    )}
                    <ListItem dense={true} sx={{ pt: 1 }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Max Temp:`}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {numeral(selectedDay.maxTempCal).format('0.0')}&#176;
                        </Typography>
                    </ListItem>
                    <ListItem dense={true}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Min Temp:`}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {numeral(selectedDay.minTempCal).format('0.0')}&#176;
                        </Typography>
                    </ListItem>
                    <ListItem dense={true}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Wind:`}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {`${selectedDay.windDir} ${numeral(selectedDay.windSpeed).format('0')} MPH`}
                        </Typography>
                    </ListItem>
                </List>
            )}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
                <SplitButton options={filterOptions} onClick={handleFilterValue} />
            </Box>
            <Divider />
            {selectedDate && <Hours id={id} date={selectedDate} filterBy={selectedFilterValue} />}
        </>
    );
};
